import { IndustrySlug } from 'containers/Setting/components/RoleFormModal/customerIntelligence';

import { LiveMetricDescriptor } from '../data/metricResult';

// The join from a shipped tile to the metric it reports. It exists nowhere else: `registry/cards.ts`
// carries `metricKeys` and no labels, formats or catalogue ids, and the catalogue Metric IDs appear
// in `src/` today only inside comments. Generated from the Overall Intelligence sheet of
// `Perx 3.0 - Layer 2 Phase 1 Metrics Catalogue.xlsx` and checked against the fixtures by
// `__tests__/metricIdentifiers.test.ts`. PLA-8815 — plan `metabase-data-seam.md` § II.12 U0.
//
// `identifier` is the catalogue Metric ID VERBATIM. The backend resolves it to an environment's
// numeric Metabase card id and the dashboard never sees one: this Metabase (v0.41.5-SNAPSHOT)
// addresses a card by number, and the cross-instance alternative needs Serialization, which is a
// Pro/Enterprise feature. So a question id in this file would be a bug, not a shortcut.
export type MetricSource =
  // The tile IS this catalogue metric. Its `metric_value` renders it directly, unless `component`
  // names one of the extra columns that metric's own question returns (`metricResult.ts`'s
  // `EXTRA_NUMERIC_COLUMNS`) -- then the tile is that column's number instead. Still `metric` and
  // not a kind of its own: one identifier, one row, read straight off the wire. A component tile
  // carries no delta, because the previous period's row is compared on `metric_value` alone.
  | { kind: 'metric'; identifier: string; component?: string }
  // The tile reads a metric whose grain is NOT scalar: one row per lifecycle stage or holding band,
  // and no `metric_value` column anywhere in the response (the backend states this itself, on a
  // marker row carrying `governed_non_scalar_grain`). Its own kind rather than a `metric` variant
  // because the scalar parse cannot read one AT ALL -- it degrades every row to `unavailable` and
  // the card then reports `insufficientData` with `calculated` as the reason.
  //
  // Without `component` the tile IS the segment set: it draws no KPI tile and reaches the card body
  // as a `CardDistribution` instead. With `component` it is one POPULATION-level extra column on
  // that same response (`EXTRA_NUMERIC_COLUMNS`), read as a single figure -- the identical
  // `component` semantics `metric` above carries, resolved through the distribution parse because
  // that is the only parse that can reach a non-scalar response's columns.
  | { kind: 'distribution'; identifier: string; component?: string }
  // The tile is a COMPONENT of this metric -- its numerator, its denominator, the cohort it is
  // taken over, the top row of its distribution, or one half of a split reading. The six-column
  // projection carries one `metric_value` per metric and none of those components, so a tile in
  // this class has no live source until that is resolved (plan § II.3, question Q-B).
  | { kind: 'derived'; from: string }
  // The tile is a QUOTIENT of two readings this card already fetches -- either two different
  // catalogue metrics, or one metric's own extra column over its own `metric_value`. Distinct from
  // `derived`, which names an UNRESOLVED gap: a `ratio` names a gap that IS resolvable from data
  // already in hand, so `assembleCard` computes it rather than marking the tile unservable. That
  // one division is arithmetic the column contract cannot avoid needing at the assembly layer --
  // the wire carries counts, and no column carries the share of one over the other.
  | {
      kind: 'ratio';
      numerator: RatioOperand;
      denominator: RatioOperand;
    }
  // The tile is the REMAINDER of one reading after another is taken out of it -- a count, not a
  // share, which is why it is not a `ratio` with the division skipped. Same operand shape and the
  // same resolver, so the two kinds cannot disagree about what makes an operand unresolvable.
  | {
      kind: 'difference';
      minuend: RatioOperand;
      subtrahend: RatioOperand;
    }
  // The tile is the POPULATION a non-scalar metric's segments partition -- the sum of `n_customers`
  // over its calculated rows. Not a `distribution` with a `component`, because no column on such a
  // response states that cohort; the sum is a reading only because the segments partition it, which
  // the metric's own governed expression has to say. A segment that carried no count leaves the
  // total UNKNOWN rather than smaller, so the tile stays empty. PLA-8815.
  | { kind: 'segmentTotal'; identifier: string }
  // No catalogue metric corresponds to this tile at all. Needs a product decision, not a lookup.
  | { kind: 'unmapped' };

// Named for `ratio`, which was the first kind to need it; `difference` takes the same shape rather
// than a parallel type, so both resolve through one helper.
export interface RatioOperand {
  /** Catalogue identifier this operand's number comes from. */
  from: string;
  /** A named extra column on `from`'s own row (see `metricResult.ts`'s allowlist) — omit to use that
   *  metric's own `metric_value` instead. */
  component?: string;
}

/**
 * One named input to a governed score, declared with the direction its OWN formula gives it.
 *
 * `effect` is read off `governed_formula_expression` and nowhere else. Two of these formulas state
 * their signs outright (O-COM-010 sums four `+` terms; O-COM-005 subtracts its penalties); the rest
 * name their inputs inside `weighted_score(...)`/`weighted_sum(...)` with no sign, and for those the
 * formula's own framing supplies it -- O-COM-022 says "Normalize each input driver to a 0-100
 * SEVERITY score", so every input raises a score that is itself bad. A column whose direction the
 * formula does not settle is left undeclared rather than guessed from its name. PLA-8815.
 */
export interface MetricFactorSpec {
  /** The `EXTRA_NUMERIC_COLUMNS` name this factor's mean arrives under. */
  column: string;
  /** The formula's own wording for the term, not a rewrite of it. */
  label: string;
  /** What this factor does to the SCORE, before the score's own polarity is applied. */
  effect: 'raises' | 'lowers';
}

/**
 * A score's contributing factors, with the polarity a factor's tone is resolved against.
 *
 * `riseIs` is deliberately NOT `riseMeans`. That field transcribes the delta chip's DRAWN hue and
 * says so at its call sites -- `follow the drawn hue rather than the intuition` -- and on two
 * metrics the drawn hue disagrees with the formula: the file paints Expectation Gap Score's `+6 pts`
 * green and Fraud / Anomaly Score's `-7 pts` `#EE6475`, both of which make `riseMeans: 'positive'`
 * the faithful reading of the design and the wrong basis for a blocker. One flag cannot be both a
 * painted hue and a semantic direction, so the factor rails read this one, off the formula, and the
 * delta chips keep theirs, off the file. PLA-8815.
 */
export interface MetricFactorSet {
  /** Whether a RISE in this score is good news, per its `governed_formula_expression`. */
  riseIs: 'good' | 'bad';
  /** The factors themselves, in the formula's own order. */
  terms: MetricFactorSpec[];
}

export interface MetricIdentity extends LiveMetricDescriptor {
  source: MetricSource;
  /**
   * Present only on the metrics whose question declares factor columns; `assembleCard` drops any the
   * response did not carry and ranks what is left by magnitude.
   */
  factors?: MetricFactorSet;
}

// Keyed by card key, in `registry/cards.ts` order, with each card's metrics in its `metricKeys`
// order -- the registry, not the payload, owns render sequence.
export const metricIdentities: Record<string, MetricIdentity[]> = {
  customer_population_activity: [
    {
      key: 'total_customers',
      label: 'Total Registered Users',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-001' }
    },
    {
      key: 'active_customers',
      label: 'Active Users',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-002' }
    },
    // Both rates below are quotients of the two readings the tiles above already fetch, so neither
    // needs a Metric ID of its own. `activity_rate` was `derived from O-COM-002` and therefore
    // permanently null; its denominator was never missing, it was simply never divided by.
    {
      key: 'activity_rate',
      label: 'Active User Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: {
        kind: 'ratio',
        numerator: { from: 'O-COM-002' },
        denominator: { from: 'O-COM-001' }
      }
    },
    // The eligible cohort rides in on O-COM-001's OWN row as `eligible_customer_count`, which the
    // parse dropped until PLA-8815. The card's own copy already names it: Subtitle "Registered
    // population, eligible coverage and meaningful active usage", Tooltip Includes "... Eligible
    // Customers ..." (`i18n/locales/en/translations.ts`).
    {
      key: 'eligible_coverage_rate',
      label: 'Eligible Coverage',
      format: 'percent',
      riseMeans: 'positive',
      source: {
        kind: 'ratio',
        numerator: { from: 'O-COM-001', component: 'eligible_customer_count' },
        denominator: { from: 'O-COM-001' }
      }
    },
    // The three legend readings the two rings are read against (`589-29436`). All three come out of
    // the same two rows the four tiles above already fetch, so none needs a Metric ID of its own.
    // The eligible COUNT, as opposed to `eligible_coverage_rate`'s share of it.
    {
      key: 'eligible_customers',
      label: 'Total Eligible Customers',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'metric',
        identifier: 'O-COM-001',
        component: 'eligible_customer_count'
      }
    },
    // Registered minus eligible. A real subtraction over two numbers on ONE row, so it cannot drift
    // from the ring beside it the way a separately fetched count could.
    {
      key: 'ineligible_customers',
      label: 'Ineligible / excluded',
      format: 'count',
      riseMeans: 'negative',
      source: {
        kind: 'difference',
        minuend: { from: 'O-COM-001' },
        subtrahend: { from: 'O-COM-001', component: 'eligible_customer_count' }
      }
    },
    // NOT a ratio, on purpose: this legend row means "share of the ELIGIBLE cohort that is active",
    // which is the INTERSECTION of eligible and active over eligible -- a joint count neither
    // O-COM-001 nor O-COM-002 states. Dividing active_customers by eligible_customers is only that
    // share if active is a SUBSET of eligible, and the catalogue never says so: eligibility (consent
    // + data availability + policy) and activity (a qualifying event) are independent filters over
    // the same registered population. Proven wrong, not just surprising, by UAT `generic`: active
    // (2,378) exceeds eligible (2,116), so the quotient reads 112.38% -- a real rate cannot exceed
    // 100%, so a number that does is evidence the formula is invalid, not evidence about the tenant.
    // Stays `unmapped` (never computed) until a metric states the joint count directly; the legend
    // row itself is unchanged so it renders the moment one does. PLA-8815.
    {
      key: 'eligible_active_rate',
      label: 'Eligible active rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    }
  ],
  data_trust_integration_health: [
    {
      key: 'data_freshness_score',
      label: 'Data Freshness Score (DFS)',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-003' }
    },
    {
      key: 'source_coverage_score',
      label: 'Source Coverage Score (SCS)',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-004' }
    },
    {
      key: 'identity_confidence',
      label: 'Identity Confidence',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-005' }
    },
    {
      key: 'integration_health_status',
      label: 'Integration Health Status',
      format: 'points',
      riseMeans: 'neutral',
      // `Text status badge` per the catalogue (Metrics Mapping with UI FR - Ov, Excel row 22); the
      // D-04 rollup answers the badge as a code, so the words are declared here. PLA-8828.
      statusCodes: { 0: 'HEALTHY', 1: 'DEGRADED', 2: 'UNHEALTHY' },
      source: { kind: 'metric', identifier: 'O-COM-006' }
    },
    {
      key: 'reversal_failure_rate',
      label: 'Reversal Failure / Retry Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-REV-008' }
    }
  ],
  customer_lifecycle_holdings: [
    // O-COM-031's stages partition this cohort -- its governed expression is `Assign exactly one
    // stage using this precedence` -- so their `n_customers` sum to it and no separate count is
    // owed. The design states the same identity: its six segments sum to the 64,800 drawn in this
    // very tile (`design/section-589-29436-gap-2026-08-11.md` § 3.4). PLA-8815.
    {
      key: 'total_eligible_customers',
      label: 'Total Eligible Customers',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'segmentTotal', identifier: 'O-COM-031' }
    },
    {
      key: 'average_active_holdings',
      label: 'Average Active Holdings',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'distribution',
        identifier: 'O-COM-032',
        component: 'average_active_holdings'
      }
    },
    // `percent`, which is what the design draws (`Multi-product Customers 41%`,
    // `design/section-589-29436-gap-2026-08-11.md` § 3.3). This shipped as a COUNT while the
    // denominator was thought to be off the wire; it is not. O-COM-032 bands each eligible customer
    // exactly once (`For each eligible customer, count distinct ...`), so its own segment counts sum
    // to the cohort -- 308 + 787 + 676 + 429 = 2,200 -- and 1,105 of them hold two or more products.
    // A component-less operand on a non-scalar metric IS that sum, so both sides come off one
    // response and cannot drift apart. PLA-8815.
    {
      key: 'multi_product_customers',
      label: 'Multi-product Customers',
      format: 'percent',
      riseMeans: 'positive',
      source: {
        kind: 'ratio',
        numerator: { from: 'O-COM-032', component: 'multi_product_customer_count' },
        denominator: { from: 'O-COM-032' }
      }
    },
    // The two segment sets the card draws below its KPI row: the stacked lifecycle bar and the
    // ranked holdings list. Neither is a tile -- `registry/cards.ts` draws exactly three, and the
    // design puts these breakdowns in charts -- so both are state-only entries whose segments reach
    // the card body through `IInsightCardBodySlots.distributions`.
    {
      key: 'lifecycle_stage_distribution',
      label: 'Lifecycle Stage Distribution',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-COM-031' }
    },
    {
      key: 'holding_band_distribution',
      label: 'Product Holding Count Distribution',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-COM-032' }
    }
  ],
  customer_engagement: [
    {
      key: 'eligible_customers',
      label: 'Eligible Customers',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-COM-011' }
    },
    {
      key: 'engaged_customers',
      label: 'Engaged Customers',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-COM-011' }
    },
    {
      key: 'engagement_rate',
      label: 'Engagement Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-011' }
    },
    {
      key: 'multi_source_coverage',
      label: 'Multi-Source Engagement Coverage',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-012' }
    },
    /* O-COM-012's other half. The same response carries a scalar `metric_status` row (the tile
       above, 97.845601 on UAT `generic`) AND three `engagement_source_family` rows -- reward
       99.860659 / platform 98.095680 / campaign 97.909893 -- and only a `distribution` tile can
       reach the second: `assembleCard` puts segments in `distributions`, never on a `CardMetric`.
       State-only, so it draws under the coverage panel and takes no place in `metricKeys`, exactly
       as the two Customer Base segment tiles do. The families OVERLAP by the response's own
       statement ("families overlap - shares do not sum to 100"), which is why the card draws them as
       independent bars rather than as one stacked split. PLA-8815. */
    {
      key: 'engagement_source_distribution',
      label: 'Engagement Source Coverage',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-COM-012' }
    }
  ],
  customer_behaviour_trends: [
    {
      key: 'behavioural_momentum',
      label: 'Behavioural Momentum Score (BMS)',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-007' }
    },
    {
      key: 'behavioural_deviation',
      label: 'Behavioural Deviation Score (BDS)',
      format: 'points',
      riseMeans: 'neutral',
      source: { kind: 'metric', identifier: 'O-COM-008' }
    },
    // `DEWS = weighted_score(recency_drop, frequency_drop, engagement_drop, sentiment_risk,
    // expectation_gap, tenure_adjustment), scaled 0-100` -- six inputs to ONE weighted score with no
    // term negated, so each raises it, and a rising DEWS is bad news (`riseMeans: 'negative'`).
    // Every factor is therefore a blocker; this score has no positive driver to state. PLA-8815.
    {
      key: 'disengagement_early_warning',
      label: 'Disengagement Early Warning Score',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-COM-009' },
      factors: {
        riseIs: 'bad',
        terms: [
          { column: 'dews_mean_recency_drop', label: 'Recency drop', effect: 'raises' },
          { column: 'dews_mean_frequency_drop', label: 'Frequency drop', effect: 'raises' },
          { column: 'dews_mean_engagement_drop', label: 'Engagement drop', effect: 'raises' },
          { column: 'dews_mean_negative_sentiment', label: 'Sentiment risk', effect: 'raises' },
          { column: 'dews_mean_expectation_gap', label: 'Expectation gap', effect: 'raises' },
          { column: 'dews_mean_tenure_adjustment', label: 'Tenure adjustment', effect: 'raises' }
        ]
      }
    },
    // The one Layer 1 score whose formula states every sign itself: `100 * (0.35*periodicity_
    // consistency + 0.25*n_repeated_event_count + 0.25*interval_variance_inverse + 0.15*tenure_norm)`
    // -- four `+` terms with positive weights, on a score a rise in is good. All four are positive
    // drivers. Labels are the formula's terms; the columns are listed in its order, not the wire's.
    // PLA-8815.
    {
      key: 'habit_strength',
      label: 'Habit Strength Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-010' },
      factors: {
        riseIs: 'good',
        terms: [
          { column: 'hss_mean_periodicity', label: 'Periodicity consistency', effect: 'raises' },
          {
            column: 'hss_mean_repeated_percentile',
            label: 'Repeated-event count',
            effect: 'raises'
          },
          {
            column: 'hss_mean_interval_consistency',
            label: 'Interval variance inverse',
            effect: 'raises'
          },
          { column: 'hss_mean_tenure_norm', label: 'Tenure norm', effect: 'raises' }
        ]
      }
    }
  ],
  engagement_funnel: [
    {
      key: 'funnel_completion_rate',
      label: 'Funnel Completion Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-013' }
    },
    // Both named columns on O-COM-013's own row, exactly as `eligible_customers` reads
    // `eligible_customer_count` off O-COM-001's -- verified live on UAT `generic` 2026-09-03:
    // `funnel_entering_customers` 2,400, `funnel_final_stage_customers` 2,108. PLA-8815.
    {
      key: 'customers_entering',
      label: 'Customers Entering',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-013', component: 'funnel_entering_customers' }
    },
    {
      key: 'final_stage_customers',
      label: 'Final-stage Customers',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-013', component: 'funnel_final_stage_customers' }
    },
    // The three mid-chain stages, same row as the two above -- verified live on UAT `generic`
    // 2026-09-08: `funnel_notification_opened_customers` 2,400, `funnel_notification_clicked_customers`
    // 2,400, `funnel_campaign_enrolled_customers` 2,384. Together with `customers_entering` and
    // `final_stage_customers` these five are monotonically non-increasing (2,400 -> 2,400 -> 2,400 ->
    // 2,384 -> 2,108) -- a real funnel over one cohort -- which is why `BehaviourEngagementSection`
    // draws its step chain from these five keys. The Figma funnel adds two outcome stages after
    // them, entered below.
    {
      key: 'notification_opened_customers',
      label: 'Notification Opened',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'metric',
        identifier: 'O-COM-013',
        component: 'funnel_notification_opened_customers'
      }
    },
    {
      key: 'notification_clicked_customers',
      label: 'Notification Clicked',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'metric',
        identifier: 'O-COM-013',
        component: 'funnel_notification_clicked_customers'
      }
    },
    {
      key: 'campaign_enrolled_customers',
      label: 'Campaign Enrolled',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'metric',
        identifier: 'O-COM-013',
        component: 'funnel_campaign_enrolled_customers'
      }
    },
    /* The chain's first and last stages, read from the columns NAMED for them rather than from the
       entry and final-stage cohorts standing in. The two pairs agree on UAT `generic` today (2,400
       and 2,108, 2026-09-08) and are not the same question: `funnel_entering_customers` is who
       entered and `funnel_final_stage_customers` is whatever this tenant's last stage turned out to
       be, while these two are the delivered and completed stages themselves. The cohort columns keep
       their own tiles in the KPI row, which is what that row asks for. PLA-8815. */
    {
      key: 'notification_delivered_customers',
      label: 'Notification Delivered',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'metric',
        identifier: 'O-COM-013',
        component: 'funnel_notification_delivered_customers'
      }
    },
    {
      key: 'campaign_action_completed_customers',
      label: 'Campaign Action Completed',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'metric',
        identifier: 'O-COM-013',
        component: 'funnel_campaign_action_completed_customers'
      }
    },
    // The Figma funnel's stages 06 / 07, read as served. On UAT digibank (2026-10-27) both exceed
    // the stage before them (2,401 issued and 2,400 redeemed after 2,084 completed), so the chain
    // rises there; `FunnelStepChain` prints no progression for a rise.
    {
      key: 'outcome_issued_customers',
      label: 'Outcome Issued',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'metric',
        identifier: 'O-COM-013',
        component: 'funnel_outcome_issued_customers'
      }
    },
    {
      key: 'outcome_redeemed_customers',
      label: 'Outcome Redeemed',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'metric',
        identifier: 'O-COM-013',
        component: 'funnel_outcome_redeemed_customers'
      }
    },
    // The catalogue's formula (Metric List, O-COM-013): customers with outcome issued / customers
    // entering the funnel * 100, both named columns on the same row.
    {
      key: 'outcome_issuance_rate',
      label: 'Outcome Issuance Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: {
        kind: 'ratio',
        numerator: { from: 'O-COM-013', component: 'funnel_outcome_issued_customers' },
        denominator: { from: 'O-COM-013', component: 'funnel_entering_customers' }
      }
    }
  ],
  current_predicted_customer_value: [
    /* A NAMED column on O-COM-028's own rows, read exactly as `average_active_holdings` is off
       O-COM-032's, and `distribution` for the same reason: O-COM-028 is the catalogue's "Current CLV
       Band Distribution" and answers non-scalar -- four `current_clv_band` rows plus the
       `governed_non_scalar_grain` marker, no `metric_value` column anywhere -- so a `metric` source
       would degrade every row to `unavailable` and leave this tile as permanently null as `derived`
       left it.

       `clv_scoreable_customer_count` (1997), NOT `clv_scored_customer_count` (1788). Both ride on
       the same rows and the gap between them is 209 real customers, so the choice is the tile's
       accuracy, not a naming preference:

       - `clv_scored_customer_count` is the band DENOMINATOR. The response's own `calculation_basis`
         states the bands as "customers per current CLV band (cohort P25/P50/P75) / scored customers
         * 100", and 447 customers x 4 bands is 1788 exactly. It answers "how many were scored", not
         "how many are eligible" -- the pipeline itself declines to call it eligible.
       - `clv_scoreable_customer_count` is the cohort that COULD be scored, which is what the
         catalogue means by eligible: "Registered customers remaining after selected filters,
         required data, applicable consent and policy rules" (Metrics Catalogue, Calculation &
         Governance Rules -> Population -> Total Eligible Customers).

       Verified live on UAT `generic` 2026-09-03, identical on all four calculated rows. The counts
       nest as the catalogue describes: 2,500 registered -> 2,116 eligible (O-COM-001) -> 1,997
       CLV-scoreable -> 1,788 actually scored. PLA-8815. */
    {
      key: 'eligible_customers',
      label: 'Eligible Customers',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'distribution',
        identifier: 'O-COM-028',
        component: 'clv_scoreable_customer_count'
      }
    },
    /* Both CLV tiles below were `derived` on a finding that the wire has since overturned: that
       NEITHER response carried a currency column at all, only `distribution_value`, `n_customers`,
       `confidence_score` and customer counts (O-COM-028 answered 23 columns and O-COM-029 22 on UAT
       `generic`, 2026-09-03). Both now answer 49 and 48, and each carries the amount its tile is
       named for -- `clv_current_mean_value` 206.985022 and `clv_predicted_mean_value` 105.611043,
       UAT `generic` 2026-09-18, identical on all four of that metric's band rows, which is the
       population-level shape `clv_scoreable_customer_count` above already reads.

       `distribution`, not `metric`, for the same reason the tile above is: both metrics answer
       non-scalar, so the scalar parse cannot reach their columns.

       NOT catalogue-labelled. The Metric List names these two "Current CLV Band Distribution" and
       "Predicted CLV Band Distribution", and the UI FR's Primary display for both is the band
       distribution plus a "value basis label" -- no amount tile. Both labels here are the Figma
       mock's (`589:27692`), shipped, and kept verbatim rather than restated from the catalogue.
       They also HARDCODE the basis: `calculation_basis` states the value basis comes from the
       ratified tenant `clv_value_basis_config` and no column reports which one was used, so "Net
       Margin" is an assertion this response does not make. PLA-8828. */
    {
      key: 'current_clv',
      label: 'Current CLV — Net Margin',
      format: 'currency',
      riseMeans: 'positive',
      source: {
        kind: 'distribution',
        identifier: 'O-COM-028',
        component: 'clv_current_mean_value'
      }
    },
    {
      key: 'predicted_clv',
      label: 'Predicted CLV — Net Margin',
      format: 'currency',
      riseMeans: 'positive',
      source: {
        kind: 'distribution',
        identifier: 'O-COM-029',
        component: 'clv_predicted_mean_value'
      }
    }
  ],
  customer_sentiment_expectations: [
    {
      key: 'sentiment_score',
      label: 'Sentiment Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-020' }
    },
    /* O-COM-020's other half, the same pairing `engagement_source_distribution` makes on O-COM-012:
       one response carrying a scalar `metric_status` row (the tile above, 40.917523 on UAT
       `generic`) and four `sentiment_class` rows -- negative 43.645484 / positive 22.575250 /
       neutral 21.070234 / mixed 12.709030. FOUR classes, and negative-dominant, where the band bar
       under the ring was drawn Positive 58 / Neutral 27 / Negative 15; the response's shape wins.
       State-only, so it takes no place in `metricKeys`. PLA-8815. */
    {
      key: 'sentiment_class_distribution',
      label: 'Sentiment Class Distribution',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-COM-020' }
    },
    {
      key: 'expectation_gap_score',
      label: 'Expectation Gap Score',
      format: 'points',
      // The DRAWN hue: the file paints this tile's `+6 pts` green. Kept, because the delta chip is
      // the design's call -- and precisely why the factor set below states its own polarity.
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-021' },
      // `EGS = weighted_score(SLA_breach, complaint_severity, negative_sentiment,
      // failed_expectation_events, unresolved_duration), scaled 0-100` -- five inputs to one score
      // with no term negated, and the score IS an expectation gap, so a rise is bad. The response
      // carries two of the five; the other three have no column and are absent, not zero.
      factors: {
        riseIs: 'bad',
        terms: [
          {
            column: 'egs_mean_delayed_attempts',
            label: 'Failed expectation events',
            effect: 'raises'
          },
          { column: 'egs_mean_negative_signals', label: 'Negative sentiment', effect: 'raises' }
        ]
      }
    },
    /* Still `unmapped`, and NOT the shell O-REV-010 was waiting for. It is a Figma tile from
       `cfd7c16e96` counting the CUSTOMERS carrying an expectation gap -- a cohort size beside the
       score that grades it. O-REV-010 measures an engagement DELTA around a reversal window; a count
       of customers and a movement in a score are different quantities in different units, so the
       tile below is its own rather than this one repointed. PLA-8828. */
    {
      key: 'expectation_gap',
      label: 'Customers with Expectation Gap',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    /* `Post-reversal engagement delta = engagement_score_after_reversal_window -
       engagement_score_before_reversal_window` -- a movement in an engagement SCORE, which is why
       `points` and not `percent`: the catalogue's second line segments it by `reversal_reason_code`,
       so the headline reading is the delta itself rather than a share of anything.

       `positive`: engagement recovering after a reversal is the good result, and the sign is the
       metric's own rather than a hue read off the file, which draws no tile here. The catalogue's
       Information Format column is empty for this id (`not in MO&RM`), so the scalar kind is read
       off the formula -- one subtraction yielding one figure. UAT answers 404 `40415`
       (2026-09-18). */
    {
      key: 'reversal_engagement_impact',
      label: 'Reversal Impact on Engagement',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-REV-010' }
    }
  ],
  reward_loyalty_intelligence: [
    /* PLA-8828: the catalogue's first four Rewards & Loyalty metrics, which this card drew NO
       reading for. They are not four more KPI tiles -- `589:28362`..`28389` still draws four, and
       `RewardsLoyaltySection` routes these to the two score grids it already heads `Reward Response
       & Dependency` (O-COM-015 / O-COM-016) and `Reward & Points Flow` (O-COM-017 / O-COM-018),
       which stood empty because "no Layer 1 metric measures reward sensitivity [or] points flow".
       These four are exactly those measurements. They are in `cards.ts`'s `metricKeys` for the same
       reason `reversal_failure_rate` is -- that list is the only one `orderMetrics` lets through to
       a body -- and the body filters them out of the KPI row.

       Live on UAT `generic` 2026-09-18: O-COM-016 0.0 and O-COM-017 64.694444 and O-COM-018
       4.961697 all `calculated`; O-COM-015 `insufficient_data`
       (`below_da4_governed_minimum_sample_or_evidence`), which the scalar parse already withholds.

       `riseMeans` transcribes the delta chip's hue, per this file's own convention. RSS rising is a
       tenant whose customers respond to incentives -- read positive. IDS rising is DEPENDENCY on
       them, which the catalogue frames as a risk -- negative. Earn-Burn rising is points earned
       outrunning points burned, which grows liability and shrinks redemption: neither direction is
       good news on its own, so it stays `neutral` rather than being guessed. */
    {
      key: 'reward_sensitivity_score',
      // `ratio`, not `points`: the catalogue's Information Format for this one is "Normalized Lift
      // Score (0-1)", and `points` would both round a 0-1 reading to one decimal and hand the score
      // dial a /100 share two orders of magnitude out. Its `metric_value` is `insufficient_data` on
      // UAT `generic` (2026-09-18), so the scale is the catalogue's statement and not an observation.
      label: 'Reward Sensitivity Score',
      format: 'ratio',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-015' }
    },
    {
      key: 'incentive_dependency_score',
      label: 'Incentive Dependency Score',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-COM-016' }
    },
    {
      key: 'reward_redemption_rate',
      label: 'Reward Redemption Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-017' }
    },
    {
      key: 'points_earn_burn_ratio',
      label: 'Points Earn-Burn Ratio',
      format: 'ratio',
      riseMeans: 'neutral',
      source: { kind: 'metric', identifier: 'O-COM-018' }
    },
    {
      key: 'reward_fulfilment_delay_rate',
      label: 'Reward Fulfilment Delay Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-COM-019' }
    },
    {
      key: 'total_reversal_event_count',
      label: 'Total Reversal Event Count',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-REV-001' }
    },
    {
      key: 'reversal_rate',
      label: 'Reversal Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-REV-002' }
    },
    {
      key: 'reversed_outcome',
      label: 'Reversed outcome',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-REV-003' }
    },
    /* O-REV-008's three-way processing split, drawn as the PROCESSING & RECONCILIATION ring rather
       than as a fifth KPI tile -- `589:28362`..`28389` draws four, so `RewardsLoyaltySection`
       withholds this one's tile. It is a RENDERED key even so: `InsightCardRenderer § orderMetrics`
       hands the body only the metrics `cards.ts` names, so a state-only entry's factors could never
       reach the ring. Same catalogue metric, key and label as Data Trust & Integration Health's own
       tile -- one metric read by two cards, each asking it a different question.
       `riseIs` reads the RATE this metric measures, `(failed + retrying) / accepted * 100`, not the
       counts: accepted rows LOWER it, retrying and failed-retrying raise it. PLA-8815. */
    {
      key: 'reversal_failure_rate',
      label: 'Reversal Failure / Retry Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-REV-008' },
      factors: {
        riseIs: 'bad',
        terms: [
          { column: 'reversal_accepted_count', label: 'Accepted', effect: 'lowers' },
          { column: 'reversal_retrying_count', label: 'Retrying', effect: 'raises' },
          {
            column: 'reversal_failed_retrying_count',
            label: 'Failed retrying',
            effect: 'raises'
          }
        ]
      }
    },
    /* O-REV-006's formula leads with `AVG(processed_at - requested_at)` and adds P50/P90/P95 and an
       SLA-breach rule over the same duration, so the headline reading is a DURATION, and the
       percentiles and the breach flag are columns this tile does not read.

       `count` is chosen because `MetricFormat` carries no duration: `percent` would assert a share
       the metric is not, `points` a governed 0-100 score it is not, and `count` is the only format
       that prints the figure without claiming a unit no response has yet stated. It is a placeholder
       for a unit, not a claim about one -- revisit the moment a wire sample names the column, which
       is also when the tile can take a `valueSuffix`. A rise is a slower reversal, so `negative`. */
    {
      key: 'reversal_processing_sla',
      label: 'Reversal Processing SLA',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-REV-006' }
    },
    /* PLA-8828: the card's three governed reversal BREAKDOWNS, state-only like every other segment
       set on this page -- they take no place in `cards.ts`'s `metricKeys` and reach the body through
       `IInsightCardBodySlots.distributions`. They fill the two bar columns and the reconciliation
       ring that `RewardsLoyaltySection` has been drawing empty ("a row here would be a row nobody
       measured").

       Their wire shape is NOT the `dimension_type`/`distribution_key`/`distribution_value` triple
       the other segment sets answer with. All three answer a governed reversal-breakdown schema --
       one grouping column, `n_events`, `share_of_events_pct` -- which `metricResult.ts`'s
       `REVERSAL_BREAKDOWN_KEY_COLUMNS` arm reads. Captured from `cols` on UAT `generic` 2026-09-18;
       all three answered ZERO rows there, so no cell of any of them has been observed populated and
       every bar below stays withheld until one is.

       O-REV-006 (Reversal Processing SLA) and O-COM-033 (Tier Membership Distribution) are the
       card's two remaining catalogue ids and are now bound below, REVERSING the call recorded here
       at 475bc43f64. That commit left them out because the catalogue marks them Phase 2 / not
       deployed and UAT answers 404 `40415` for each (2026-09-18); the decision since taken is that
       every catalogue metric with a card gets its binding whether or not this deploy serves it, and
       a 40415 is a DESIGNED empty state the screen already renders -- `data/cardStateFromOutcome.ts`
       turns it into `awaitingData` and `assembleCard` puts it on the asking tile, exactly as it does
       for C-COM-025 / C-COM-026 on Customer 360, which 404 by design and have shipped that way. The
       tenant industry can be switched and the data side is redeploying, so the binding is what makes
       the reading appear with no edit here. There is NO wire sample for either -- no `cols`, no
       column names -- so both source kinds are read off the catalogue's own Information Format and
       are the one claim in this block that a live response has not confirmed. PLA-8828. */
    {
      key: 'reversal_entitlement_distribution',
      label: 'Reversal Breakdown by Entitlement Type',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-REV-004' }
    },
    {
      key: 'reversal_reason_distribution',
      label: 'Reversal Reason Distribution',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-REV-005' }
    },
    {
      key: 'reversal_reconciliation_distribution',
      label: 'Reversal Reconciliation Status',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-REV-007' }
    },
    /* The catalogue's Information Format for O-COM-033 is `Distribution` and its formula groups
       customers by configured tier, so this is a SEGMENT SET like the three above and not a fifth
       KPI tile: the card already draws a tier donut, which until now plotted nothing because no
       metric was behind it (`RewardsLoyaltySection`'s `TIER_ROWS`). State-only for that reason --
       absent from `cards.ts`'s `metricKeys`, reaching the body through
       `IInsightCardBodySlots.distributions`.

       Which segment schema it answers with is UNVERIFIED: the standard
       `dimension_type`/`distribution_key`/`distribution_value` triple is assumed, because the
       bespoke `share_of_events_pct` arm is a reversal-family schema and this is not a reversal
       metric. `distributionFromEnvelope` detects on the COLUMN SET rather than on the identifier, so
       if it answers the triple the donut fills with no edit here; if it answers something else, this
       entry is where the second arm goes. `percent` because the formula's own reading is
       `tier_customer_count / total_eligible_tier_members * 100`. PLA-8828. */
    {
      key: 'tier_membership_distribution',
      label: 'Tier Membership Distribution',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-COM-033' }
    }
  ],
  risk_unusual_activity: [
    {
      key: 'fraud_anomaly_score',
      label: 'Fraud / Anomaly Score (FAS)',
      format: 'points',
      // The DRAWN hue: the file paints this tile's `-7 pts` `#EE6475`
      // (`design/section-risk-protection-devmode-2026-08-12.md` § 3), so a FALL reads negative and a
      // rise positive. Kept as the design's call, which is why the factor set states its own
      // polarity below rather than borrowing this one.
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-022' },
      // `FAS = weighted_sum(velocity_zscore, duplicate_count, callback_mismatch, device_risk,
      // amount_outlier, identity_conflict), capped 0-100`, and the formula's next line settles the
      // direction outright: `Normalize each input driver to a 0-100 SEVERITY score`. Every term
      // raises severity, so every one is a blocker. Labels are the ring's own drawn rows
      // (`589-29271` § 4.2), which name the same six terms. PLA-8815.
      factors: {
        riseIs: 'bad',
        terms: [
          { column: 'fas_mean_factor_velocity_z', label: 'Transaction velocity', effect: 'raises' },
          { column: 'fas_mean_factor_amount_outlier', label: 'Amount outlier', effect: 'raises' },
          {
            column: 'fas_mean_factor_duplicate_issuance',
            label: 'Duplicate activity',
            effect: 'raises'
          },
          { column: 'fas_mean_factor_device_ip', label: 'Device/IP risk', effect: 'raises' },
          {
            column: 'fas_mean_factor_callback_mismatch',
            label: 'Callback mismatch',
            effect: 'raises'
          },
          {
            column: 'fas_mean_factor_identity_conflict',
            label: 'Identity conflict',
            effect: 'raises'
          }
        ]
      }
    },
    // A NAMED column on O-COM-023's own rows, read exactly as `average_active_holdings` is off
    // O-COM-032's. `distribution` rather than `metric` because O-COM-023 answers non-scalar -- it is
    // the catalogue's "Risk Segment Distribution", one row per risk band with no `metric_value`
    // column anywhere -- so a `metric`-kind source here would degrade every row to `unavailable` and
    // leave the tile permanently null, which is what `derived` was doing before. Verified live on
    // UAT `generic` 2026-09-03: `high_risk_customer_count` reads 0, a real calculated zero rather
    // than an absence. `count`, not `percent`: it is a count of customers. PLA-8815.
    {
      key: 'high_risk_customers',
      label: 'High-risk Customers',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'distribution',
        identifier: 'O-COM-023',
        component: 'high_risk_customer_count'
      }
    },
    /* Still `unmapped`, and NOT the shell O-REV-009 was waiting for. Both were read off the Figma
       tile row in `cfd7c16e96`, the original tile-to-metric join, and this one asks how confident
       the RISK BAND SPLIT beside it is -- a confidence in O-COM-023's own distribution. O-REV-009
       is a suspicion score over reversal events, a different reading with a different population, so
       pointing this tile at it would put one metric's number under another metric's label. It gets
       its own tile below. PLA-8828. */
    {
      key: 'risk_distribution_confidence',
      label: 'Risk-distribution Confidence',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'unmapped' }
    },
    /* `Weighted score = velocity_weight + unmatched_original_weight + post_redemption_weight +
       high_value_weight + repeated_actor_weight + callback_mismatch_weight, scaled 0-100` -- the
       same governed 0-100 shape as the FAS tile that heads this card, so `points`. No factor set:
       the six weights are the formula's terms, but no column of any response has been seen carrying
       them, and `factors` is read off named columns rather than off a formula. A rise is more
       suspicion, so `negative` -- stated from the formula, not transcribed from a drawn hue, because
       the file draws no tile for this metric at all. UAT answers 404 `40415` (2026-09-18). */
    {
      key: 'suspicious_reversal_pattern_score',
      label: 'Suspicious Reversal Pattern Score',
      format: 'points',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-REV-009' }
    },
    // The band split the right-hand ring divides, off the SAME response the tile above reads one
    // column of. A state-only entry with no tile, exactly as the two Customer Lifecycle & Product
    // Holdings segment sets are: `registry/cards.ts` draws three tiles on this card and puts the
    // band breakdown in a donut beside them, so a fourth tile is not an option -- but the segments
    // are real readings and reach the card body through `IInsightCardBodySlots.distributions`.
    {
      key: 'risk_segment_distribution',
      label: 'Risk Segment Distribution',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-COM-023' }
    }
  ],
  debt_spending_health: [
    {
      key: 'essential_discretionary_ratio',
      label: 'Essential vs Discretionary Spend Ratio',
      format: 'ratio',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-BFSI-005' }
    },
    {
      key: 'debt_obligation_signal_rate',
      label: 'Debt Obligation Signal Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-BFSI-006' }
    },
    {
      key: 'arrears_signal_rate',
      label: 'Arrears Signal Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-BFSI-007' }
    }
  ],
  income_cashflow_health: [
    {
      key: 'income_detection_coverage',
      label: 'Salary / Income Detection Coverage',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-BFSI-001' }
    },
    {
      key: 'income_stability_score',
      label: 'Income Stability Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-BFSI-002' }
    },
    /* The catalogue's third Income & Cashflow metric, in its own position between the stability
       score and the forecast risk rate. It answers on UAT `generic` (2026-09-18) with the standard
       scalar row and `calculation_status: not_applicable` / `industry_out_of_scope` -- a governed
       refusal for a non-Banking tenant, which the scalar parse already reports as a withheld
       reading rather than a zero. `points`, the format every 0-100 governed score on this page
       takes. PLA-8828.

       O-BFSI-001..004 are the ONLY four this card's catalogue row names, so the card is complete
       with this entry. O-BFSI-008/009/010 are provisioned on the endpoint but answer `403` on UAT
       even for the tenant's own industry (2026-09-18), and the final catalogue's Metric List and
       Overall UI tab name neither them nor a card for them -- not a gap on this card. */
    {
      key: 'cashflow_health_score',
      label: 'Cashflow Health Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-BFSI-003' }
    },
    {
      key: 'cashflow_forecast_risk_rate',
      label: 'Cashflow Forecast Risk Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-BFSI-004' }
    }
  ],
  churn_service_experience: [
    {
      key: 'service_experience_risk_rate',
      label: 'Network / Service Experience Risk Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-TEL-005' }
    },
    {
      key: 'subscribers_with_service_risk',
      label: 'Subscribers with Service Risk',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'derived', from: 'O-TEL-005' }
    },
    {
      key: 'telco_churn_risk_rate',
      label: 'Telco Churn Risk Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-TEL-006' }
    },
    {
      key: 'high_risk_active_subscribers',
      label: 'High-risk Active Subscribers',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'derived', from: 'O-TEL-006' }
    }
  ],
  plan_usage_revenue: [
    {
      key: 'current_arpu',
      label: 'Current ARPU',
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-TEL-001' }
    },
    {
      key: 'total_billable_revenue',
      label: 'Total Billable Revenue',
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-TEL-001' }
    },
    {
      key: 'data_utilisation_rate',
      label: 'Data Utilization Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-TEL-002' }
    },
    {
      key: 'voice_utilisation_rate',
      label: 'Voice Utilization Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-TEL-002' }
    }
  ],
  recharge_upgrade_readiness: [
    {
      key: 'recharge_regularity_score',
      label: 'Recharge Regularity Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-TEL-003' }
    },
    {
      key: 'regular_recharge_subscribers',
      label: 'Regular Recharge Subscribers',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-TEL-003' }
    },
    {
      key: 'plan_upgrade_readiness_rate',
      label: 'Plan Upgrade Readiness Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-TEL-004' }
    },
    {
      key: 'upgrade_growth_planner_eligible',
      label: 'Growth Planner Eligible',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-TEL-004' }
    }
  ],
  policy_health_claims: [
    {
      key: 'policy_active_rate',
      label: 'Policy Active Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-INS-001' }
    },
    {
      key: 'active_policies',
      label: 'Active Policies',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-INS-001' }
    },
    {
      key: 'premium_payment_health',
      label: 'Premium Payment Health',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-INS-002' }
    },
    {
      key: 'claims_frequency',
      label: 'Claims Frequency',
      format: 'ratio',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-INS-004' }
    }
  ],
  renewal_growth_opportunities: [
    {
      key: 'renewal_risk_rate',
      label: 'Renewal Risk Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-INS-003' }
    },
    {
      key: 'coverage_gap_signal_rate',
      label: 'Coverage Gap Signal Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-INS-005' }
    },
    {
      key: 'rider_cross_sell_readiness',
      label: 'Rider / Cross-sell Readiness',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-INS-006' }
    },
    {
      key: 'growth_planner_eligible',
      label: 'Growth Planner Eligible',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    }
  ],
  wallet_activity_funding: [
    {
      key: 'wallet_active_user_rate',
      label: 'Wallet Active User Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-FIN-001' }
    },
    {
      key: 'active_eligible_users',
      label: 'Active / Eligible Users',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-FIN-001' }
    },
    {
      key: 'topup_frequency_score',
      label: 'Top-up Frequency Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-FIN-002' }
    },
    {
      key: 'highest_feature_adoption',
      label: 'Highest Feature Adoption',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-FIN-004' }
    }
  ],
  payment_usage_risk: [
    {
      key: 'payment_diversity_index',
      label: 'Payment Diversity Index',
      format: 'ratio',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-FIN-003' }
    },
    {
      key: 'diversity_cohort',
      label: 'Diversity Cohort',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-FIN-003' }
    },
    {
      key: 'wallet_velocity_anomaly_rate',
      label: 'Wallet Velocity Anomaly Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-FIN-005' }
    },
    {
      key: 'anomalous_wallet_users',
      label: 'Anomalous Wallet Users',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'derived', from: 'O-FIN-005' }
    }
  ],
  merchant_category_data_quality: [
    {
      key: 'merchant_identification_rate',
      label: 'Merchant identification rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-HYB-001' }
    },
    {
      key: 'category_classification_coverage',
      label: 'Category classification coverage',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-HYB-002' }
    },
    /* PLA-8828: the catalogue's O-HYB-003, in its own position between the coverage rate above and
       the unclassified rate below. `negative` because the rate counts transactions the classifier
       was NOT sure about -- a rise is quality falling, the same direction the unclassified rate
       carries. Live on UAT `generic` 2026-09-18: 23.893316, `calculated`. */
    {
      key: 'low_confidence_transaction_rate',
      label: 'Low confidence transaction rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-HYB-003' }
    },
    {
      key: 'unclassified_transaction_rate',
      label: 'Unclassified transaction rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-HYB-004' }
    },
    /* PLA-8828: O-HYB-010, the card's own confidence score, between O-HYB-004 and O-HYB-011 as the
       catalogue orders it. `points`, not `percent`: the wire answers 0.759909 on a 0-1 scale (UAT
       `generic` 2026-09-18, `calculated`) and this page's percent format is for the 0-100 rates. */
    {
      key: 'merchant_category_confidence_score',
      label: 'Merchant / Category Confidence Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-HYB-010' }
    },
    {
      key: 'merchant_alias_match_rate',
      label: 'Merchant alias match rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-HYB-011' }
    },
    {
      key: 'gateway_masking_resolution_rate',
      label: 'Gateway masking resolution rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-HYB-012' }
    }
  ],
  merchant_category_preferences: [
    {
      key: 'top_merchant_affinity',
      label: 'Top Merchant Affinity',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-HYB-013' }
    },
    {
      key: 'top_spend_category',
      label: 'Top Spend Category',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-HYB-014' }
    },
    {
      key: 'avg_spend_per_active_user',
      label: 'Average Spend per Active User',
      format: 'currency',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-HYB-015' }
    },
    // The two segment sets this card draws below its KPI row, state-only exactly as Customer
    // Lifecycle & Product Holdings' two are: `registry/cards.ts` draws three tiles here, and neither
    // breakdown is one of them.
    //
    // The two KPI tiles above stay `derived` on purpose. Each is a NAME paired with a share
    // (`FairPrice` 18.4%, `Groceries` 24.6%) and `CardMetric` carries one number, so the head of a
    // distribution is still not a tile this contract can fill -- and for `top_spend_category` the
    // name would additionally be the wrong one; see the reward-view note below. PLA-8815.
    {
      key: 'merchant_affinity_distribution',
      label: 'Merchant Affinity Distribution',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-HYB-013' }
    },
    /* NOT `top_spend_category`'s spend view, and labelled for what the response actually carries.
       O-HYB-014 declares TWO views of itself and UAT `generic` answers with the reward one, stated
       three independent ways on every calculated row (2026-09-03): `dimension_type` is
       `category_affinity_reward`; `calculation_basis` reads "D-07 reward view (fully governed):
       successful categorized redemptions per reward category / all successful categorized
       redemptions * 100"; and the `distribution_key` vocabulary is voucher / badge / benefit /
       points / premium_discount / data_pass / cashback / bill_credit -- Perx reward categories, not
       merchant spend categories. `governed_formula_expression` describes the OTHER view ("Spend
       view: CategoryShare_c = eligible spend in category c / total classified eligible spend"), and
       no row of it came back.

       So these segments are not what a tile called "Top Spend Category" reports, and drawing them
       under that name would misreport reward redemption as spend. The card's own why-box already
       draws the line -- "reward-redemption preference remains a separate view and is never blended
       into spend affinity" -- and the design's `CATEGORY AFFINITY` switch already carries a
       `Reward Redemption` pill with no series behind it. This entry is that view's data. Which of
       the two views the KPI tile should read is a product decision, so the tile is left alone. */
    {
      key: 'reward_category_affinity_distribution',
      label: 'Reward Category Affinity',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-HYB-014' }
    }
  ],
  payment_behaviour_cash_usage: [
    {
      key: 'qr_payment_ratio',
      label: 'QR Payment Ratio',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-HYB-005' }
    },
    {
      key: 'cash_withdrawal_dependency_score',
      label: 'Cash Withdrawal Dependency Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-HYB-006' }
    },
    {
      key: 'transfer_vs_spend_ratio',
      label: 'Transfer vs Spend Ratio',
      format: 'ratio',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-HYB-007' }
    },
    /* The intent split the card draws under its KPI row, state-only for the same reason the two
       above are: `registry/cards.ts` draws three tiles here and puts this breakdown in a segmented
       bar beside them.

       O-HYB-007 is the metric that split, not a second one: its own
       `governed_formula_expression` reads "Transfer vs Spend Ratio = SUM/COUNT by
       transaction_intent_class / SUM/COUNT all eligible transactions. Intent classes:
       MERCHANT_SPEND, BILL_PAYMENT, P2P_TRANSFER, P2M_TRANSFER, SELF_TRANSFER, CASH_WITHDRAWAL,
       INVESTMENT, REFUND_REVERSAL, UN[CLASSIFIED]" -- one row per intent class, which is exactly
       what `PAYMENT INTENT MIX` draws. The tile above stays `derived`: a ratio of transfers to
       spend is a quotient over two GROUPS of those classes, and no column states which classes fall
       on which side of it.

       UAT `generic` returns the marker row alone today (2026-09-03) -- `enabled_not_aggregated` /
       `governed_non_scalar_grain`, no calculated rows at all -- so this draws nothing yet and
       nothing about the card changes. It is wired now because the mechanism is self-correcting: the
       block appears the moment real class rows arrive, with no edit here. PLA-8815. */
    {
      key: 'payment_intent_distribution',
      label: 'Payment Intent Mix',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-HYB-007' }
    },
    /* PLA-8828: the card's last two catalogue ids, both state-only segment sets for the same reason
       the intent mix above is. O-HYB-009 IS the `Payment Rail / Channel Mix` panel this section
       already heads and fills from `railRowsFor`, a two-row QR / non-QR split derived from
       O-HYB-005's own reading -- the governed mix supersedes that derivation the moment it answers.
       O-HYB-008 is the `P2P vs P2M Mix Ratio` named in `PAYMENT_INTENT_SIGNALS`' comment as one of
       the three readings O-HYB-007's classes do not carry; it is its own metric and this is it.

       Both answered the marker row ALONE on UAT `generic` 2026-09-18 -- `enabled_not_aggregated` /
       `governed_non_scalar_grain`, zero calculated rows -- so both draw nothing today and the panels
       keep exactly the content they have. Same self-correcting wiring as O-HYB-007. */
    {
      key: 'p2p_p2m_mix_distribution',
      label: 'P2P vs P2M Mix Ratio',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-HYB-008' }
    },
    {
      key: 'payment_rail_distribution',
      label: 'Payment Rail / Channel Mix',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-HYB-009' }
    }
  ],
  product_readiness: [
    {
      key: 'eligible_customers',
      label: 'Eligible Customers',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'eligible_customer_rate',
      label: 'Eligible Customer Rate',
      format: 'percent',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'product_gap_customers',
      label: 'Product-gap Customers',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'unmapped' }
    },
    {
      key: 'total_evaluated',
      label: 'Total Evaluated',
      format: 'count',
      riseMeans: 'neutral',
      source: { kind: 'unmapped' }
    }
  ],
  growth_opportunities_product_interest: [
    {
      key: 'opportunity_signal_count',
      label: 'Opportunity Signal Count',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-024' }
    },
    // O-COM-024's OWN row states this count (`opportunity_active_customer_count`, 0 on UAT
    // `generic` 2026-09-08 -- a calculated zero, not an absence). It was declared `derived` from
    // O-COM-025, which could never have filled a COUNT tile: that metric's grain is not scalar and
    // it carries no `metric_value` column at all. (The 2026-09-08 note here read "answers nothing on
    // any environment"; re-probed on UAT `generic` 2026-09-18 it answers 36 calculated
    // `product_propensity_band` rows, which is what the segment set below now draws.) PLA-8815,
    // PLA-8828.
    {
      key: 'customers_with_active_signals',
      label: 'Customers with Active Signals',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'metric',
        identifier: 'O-COM-024',
        component: 'opportunity_active_customer_count'
      }
    },
    /* PLA-8828: O-COM-025, the card's second catalogue id and the source the `PRODUCT LEVEL
       PROPENSITY DISTRIBUTION` strip was drawing a fixture for. State-only -- `registry/cards.ts`
       draws two tiles here and the catalogue's own Information Format for this metric is
       "Propensity Distribution", not a KPI.

       36 calculated rows on UAT `generic` 2026-09-18, split on `product_propensity_band`, with keys
       of the form `<product>:<band>` (`fintech_wallet_savings_pocket:insufficient`, 43.75,
       `n_customers` 119). The 37th row is the marker the parse excludes. */
    {
      key: 'product_propensity_distribution',
      label: 'Product-Level Propensity Distribution',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'distribution', identifier: 'O-COM-025' }
    }
  ],
  contact_restrictions: [
    {
      key: 'suppression_rate',
      label: 'Suppression Rate',
      format: 'percent',
      riseMeans: 'negative',
      source: { kind: 'metric', identifier: 'O-COM-027' }
    },
    {
      key: 'suppressed_customers',
      label: 'Suppressed Customers / Signals',
      format: 'count',
      riseMeans: 'negative',
      source: { kind: 'derived', from: 'O-COM-027' }
    },
    {
      key: 'eligible_customers',
      label: 'Eligible Customer',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'derived', from: 'O-COM-027' }
    }
  ],
  ready_for_engagement: [
    // `NRS = weighted_score(propensity, confidence, recency, channel_reachability, consent_allowed,
    // no_fatigue, no_high_risk)` -- seven positively-framed terms (the last two are already
    // inverted by their own names) on a score a rise in is good, so each raises it. The response
    // carries six of the seven; `consent_allowed` has no column and is simply absent from the rail
    // rather than drawn at zero. PLA-8815.
    {
      key: 'nudge_readiness_score',
      label: 'Nudge Readiness Score',
      format: 'points',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-026' },
      factors: {
        riseIs: 'good',
        terms: [
          { column: 'nrs_mean_component_propensity', label: 'Propensity', effect: 'raises' },
          { column: 'nrs_mean_component_confidence', label: 'Confidence', effect: 'raises' },
          { column: 'nrs_mean_component_recency', label: 'Recency', effect: 'raises' },
          {
            column: 'nrs_mean_component_reachability',
            label: 'Channel reachability',
            effect: 'raises'
          },
          { column: 'nrs_mean_component_no_fatigue', label: 'No fatigue', effect: 'raises' },
          { column: 'nrs_mean_component_no_risk', label: 'No high risk', effect: 'raises' }
        ]
      }
    },
    // O-COM-026's own row states this count -- `nrs_ready_customer_count`, 624 on UAT `generic`
    // (2026-09-08) -- so the tile reads a named column rather than waiting on a cohort nothing
    // carries. Its sibling `nrs_evaluable_customer_count` (2,169) is the denominator the card's
    // rate is taken over and is not this tile. PLA-8815.
    {
      key: 'ready_customers',
      label: 'Ready Customers',
      format: 'count',
      riseMeans: 'positive',
      source: {
        kind: 'metric',
        identifier: 'O-COM-026',
        component: 'nrs_ready_customer_count'
      }
    },
    // State-only, outside `metricKeys`: the denominator the Ready Customers overlay heads with.
    // PLA-8828.
    {
      key: 'ready_evaluable_customers',
      label: 'Evaluable Customers',
      format: 'count',
      riseMeans: 'neutral',
      source: {
        kind: 'metric',
        identifier: 'O-COM-026',
        component: 'nrs_evaluable_customer_count'
      }
    },
    // Stays `unmapped`, not a guess: O-COM-026 DOES carry a real candidate column,
    // `confidence_score` (0.9276 on UAT `generic`, 2026-09-03) -- but it is a 0-1 fraction, and this
    // tile's `percent` format expects the 0-100 scale every other percent tile in this file already
    // stores (`activity_rate` 95.12, not 0.9512). The `metric`+`component` read has no scale step --
    // it is a verbatim passthrough, correct for every column it reads today because none of them
    // needed one -- so wiring this straight would render `0.9%` for a 93% confidence, silently wrong
    // in the one direction a reader could not catch by eye. Needs a scale-aware read added to that
    // mechanism, not a registry entry guessing around its absence. PLA-8815.
    {
      key: 'result_confidence',
      label: 'Result Confidence',
      format: 'percent',
      riseMeans: 'neutral',
      source: { kind: 'unmapped' }
    }
  ],
  growth_planner_handoff_readiness: [
    {
      key: 'handoff_eligible_signals',
      label: 'Growth Planner Handoff Eligible',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-030' }
    },
    // `handoff_blocked_count` on O-COM-030's own row, 327 on UAT `generic` (2026-09-08). Read as
    // the total, never rebuilt from its two by-REASON siblings (`..._low_confidence_count` 52,
    // `..._missing_consent_count` 49): those name causes and 52 + 49 is not 327, so the reasons are
    // neither exhaustive nor exclusive. PLA-8815.
    {
      key: 'handoff_blocked_signals',
      label: 'Not Eligible / Blocked',
      format: 'count',
      riseMeans: 'negative',
      source: {
        kind: 'metric',
        identifier: 'O-COM-030',
        component: 'handoff_blocked_count'
      }
    },
    /* The card's Top Blockers rail, which stood over an `AwaitingData` line. Two REASONS off
       O-COM-030's own row -- 52 and 49 on UAT `generic` 2026-09-18 -- for the "main contributing
       reason" its UI FR asks the card to display, and whose Supporting list names both by name
       ("confidence score", "customer consent and permitted-purpose status").

       State-only, taking no place in `metricKeys`: `registry/cards.ts` draws this card's two KPI
       tiles from `handoff_eligible_signals` / `handoff_blocked_signals`, and these two are rail
       rows, not a third and fourth tile. Labels are the governed formula's own terms ("Eligible if
       ... AND confidence >= threshold AND consent_allowed AND ..."), the same rule the EGS and FAS
       factor sets label by, because the catalogue names no per-blocker copy. PLA-8828. */
    {
      key: 'handoff_blocked_low_confidence',
      label: 'Confidence below threshold',
      format: 'count',
      riseMeans: 'negative',
      source: {
        kind: 'metric',
        identifier: 'O-COM-030',
        component: 'handoff_blocked_low_confidence_count'
      }
    },
    {
      key: 'handoff_blocked_missing_consent',
      label: 'Consent not allowed',
      format: 'count',
      riseMeans: 'negative',
      source: {
        kind: 'metric',
        identifier: 'O-COM-030',
        component: 'handoff_blocked_missing_consent_count'
      }
    },
    // State-only like the two above: O-COM-030's remaining reasons and the totals the handoff
    // overlays head with. PLA-8828.
    {
      key: 'handoff_blocked_incomplete_evidence',
      label: 'Evidence incomplete',
      format: 'count',
      riseMeans: 'negative',
      source: {
        kind: 'metric',
        identifier: 'O-COM-030',
        component: 'handoff_blocked_incomplete_evidence_count'
      }
    },
    {
      key: 'handoff_blocked_dnn_active',
      label: 'Do-not-nudge active',
      format: 'count',
      riseMeans: 'negative',
      source: {
        kind: 'metric',
        identifier: 'O-COM-030',
        component: 'handoff_blocked_dnn_active_count'
      }
    },
    {
      key: 'handoff_eligible_customers',
      label: 'Handoff Eligible',
      format: 'count',
      riseMeans: 'positive',
      source: { kind: 'metric', identifier: 'O-COM-030', component: 'handoff_eligible_count' }
    },
    {
      key: 'handoff_evaluated_customers',
      label: 'Evaluated for Handoff',
      format: 'count',
      riseMeans: 'neutral',
      source: { kind: 'metric', identifier: 'O-COM-030', component: 'handoff_evaluated_count' }
    }
  ]
};

export const metricIdentitiesFor = (cardKey: string): MetricIdentity[] =>
  Object.prototype.hasOwnProperty.call(metricIdentities, cardKey) ? metricIdentities[cardKey] : [];

export const PRODUCT_READINESS_CARD_KEY = 'product_readiness';

/**
 * Product Readiness is the one card whose catalogue metric is NOT fixed by its card key. The screen
 * reports on one product at a time — the band dropdown's selection — and the Metric List gives every
 * product of every industry its own `O-*-PR-*` id, named "{Product} Readiness - Portfolio
 * Readiness". So the `product_readiness` entry above cannot name an identifier: it owns the four
 * tiles' labels, formats and polarities, and `productReadinessIdentitiesFor` stamps the selected
 * product's id onto them.
 *
 * Keyed by industry, then by `registry/productReadiness.ts` option id in that catalogue's own order,
 * one row per Metric List row. Overall Intelligence (`O-`) only — the parallel `C-*-PR-*` set is the
 * Customer 360 screen's (PLA-8828) and is not this card's. PLA-8815.
 */
export const PRODUCT_READINESS_METRIC_IDS: Record<IndustrySlug, Record<string, string>> = {
  banking_bfsi: {
    credit_card_limit_upgrade: 'O-BFS-PR-001',
    personal_loan: 'O-BFS-PR-002',
    mortgage_home_loan: 'O-BFS-PR-003',
    auto_secured_loan: 'O-BFS-PR-004',
    overdraft_credit_line: 'O-BFS-PR-005',
    savings_account_deposit_product: 'O-BFS-PR-006',
    term_deposit: 'O-BFS-PR-007',
    investment_wealth_product: 'O-BFS-PR-008',
    insurance_cross_sell: 'O-BFS-PR-009'
  },
  fintech_wallet_payment: {
    wallet_activation_reactivation: 'O-FIN-PR-001',
    wallet_top_up: 'O-FIN-PR-002',
    bill_payment_adoption: 'O-FIN-PR-003',
    p2p_p2m_qr_adoption: 'O-FIN-PR-004',
    wallet_linked_card: 'O-FIN-PR-005',
    bnpl_credit: 'O-FIN-PR-006',
    savings_investment_feature: 'O-FIN-PR-007',
    insurance_protection_feature: 'O-FIN-PR-008',
    merchant_business_services: 'O-FIN-PR-009'
  },
  insurance: {
    policy_renewal: 'O-INS-PR-001',
    coverage_top_up: 'O-INS-PR-002',
    rider_add_on: 'O-INS-PR-003',
    life_insurance_cross_sell: 'O-INS-PR-004',
    health_insurance_cross_sell: 'O-INS-PR-005',
    motor_insurance_cross_sell: 'O-INS-PR-006',
    travel_insurance: 'O-INS-PR-007',
    home_property_insurance: 'O-INS-PR-008',
    premium_payment_plan: 'O-INS-PR-009',
    lapse_prevention_service_recovery: 'O-INS-PR-010'
  },
  telco: {
    mobile_plan_upgrade: 'O-TEL-PR-001',
    data_pack_add_on: 'O-TEL-PR-002',
    roaming_pack: 'O-TEL-PR-003',
    device_upgrade_financing: 'O-TEL-PR-004',
    family_multi_line_plan: 'O-TEL-PR-005',
    broadband_fibre: 'O-TEL-PR-006',
    content_ott_bundle: 'O-TEL-PR-007',
    retention_renewal_offer: 'O-TEL-PR-008'
  }
};

// One flat index across the four industries. The 36 product ids are globally unique — asserted in
// `__tests__/metricIdentifiers.test.ts`, which is what makes a lookup by product alone unambiguous —
// so the seam resolves a selection without the tenant industry having to travel down to the fetch
// alongside it. A collision added later fails that test rather than silently resolving to whichever
// industry was declared last.
const PRODUCT_READINESS_METRIC_ID_BY_PRODUCT: Record<string, string> = Object.values(
  PRODUCT_READINESS_METRIC_IDS
).reduce((acc, byProduct) => ({ ...acc, ...byProduct }), {});

/**
 * True for a Product Readiness metric id, in EITHER catalogue — Layer 1's `O-*-PR-*` and Customer
 * 360's `C-*-PR-*` are the same questions asked over the base and over one customer.
 *
 * Matched on the shape rather than looked up in `PRODUCT_READINESS_METRIC_IDS`, because that record
 * holds the `O-` half only; `__tests__/metricIdentifiers.test.ts` asserts every id in it matches, so
 * the pattern is proved against the known set instead of standing on its own. PLA-8815.
 */
export const isProductReadinessIdentifier = (identifier: string): boolean =>
  /-PR-\d{3}$/.test(identifier);

/**
 * The four Product Readiness tiles, pointed at the selected product's metric.
 *
 * All four read the SAME id. The Metric List gives a product ONE readiness metric, and the card's
 * with/without-holding split is a presentational split of that one payload's columns rather than two
 * metrics — so a second id per branch would be invented, not looked up.
 *
 * All four are `metric` too. The rate, the product gap and the evaluated base were `derived`, the
 * class for a tile the six-column projection carries no column for — which made them permanently
 * `notConfigured` in `assembleCard`. This schema is not that projection: every one of the four is a
 * sum over the response's own `n_customers` cells (`data/productReadinessMetric.ts`), so the tile
 * that reads it has a source, and pointing it at one is what let the card stop drawing "Not
 * Configured" over 434 populated rows. PLA-8815.
 *
 * An unknown or absent product — nothing selected yet, or an industry with no catalogue — falls back
 * to the unmapped entry, so the card states "Not Configured" instead of fetching a guess.
 */
export const productReadinessIdentitiesFor = (productId?: string): MetricIdentity[] => {
  const tiles = metricIdentities[PRODUCT_READINESS_CARD_KEY];
  // `hasOwnProperty.call` rather than a truthiness test on the index: a product of 'constructor'
  // would otherwise resolve a prototype member into an identifier.
  const identifier =
    productId &&
    Object.prototype.hasOwnProperty.call(PRODUCT_READINESS_METRIC_ID_BY_PRODUCT, productId)
      ? PRODUCT_READINESS_METRIC_ID_BY_PRODUCT[productId]
      : undefined;

  if (!identifier) {
    return tiles;
  }

  return tiles.map(tile => ({ ...tile, source: { kind: 'metric' as const, identifier } }));
};

/**
 * The render-time parameters that decide WHICH metric a card reads — as opposed to which cache entry
 * its answer lands in, which is `cacheKeyFor`'s job. Product Readiness is the only card with any.
 */
export interface CardMetricScope {
  /** `registry/productReadiness.ts` option id — the band dropdown's selection. */
  product?: string;
}

/**
 * `metricIdentitiesFor` plus the one card that resolves its metric at render time.
 *
 * Every other card ignores `scope` and answers exactly as it did before this existed, which is why
 * `metricIdentitiesFor` above keeps its signature: it is the static join, used by the tests and by
 * anything asking what a card declares rather than what one render of it reads.
 */
export const metricIdentitiesForCard = (
  cardKey: string,
  scope?: CardMetricScope
): MetricIdentity[] =>
  cardKey === PRODUCT_READINESS_CARD_KEY
    ? productReadinessIdentitiesFor(scope?.product)
    : metricIdentitiesFor(cardKey);

/**
 * The 36 product-scoped ids, sorted — Product Readiness' half of the provisioning ask.
 *
 * Deliberately NOT folded into `liveIdentifiers` below: that list is what one page view needs a
 * reading from, and a page view reads exactly one of these (the tenant's industry, the reader's
 * selection). Provisioning still needs all 36, so they are exported rather than lost.
 */
export const productReadinessIdentifiers = (): string[] =>
  Array.from(new Set(Object.values(PRODUCT_READINESS_METRIC_ID_BY_PRODUCT))).sort();

/**
 * Every catalogue metric the shipped page needs a reading from, deduplicated — the fetch set, and
 * the provisioning ask.
 *
 * Includes the `derived` entries' source metrics, which is the whole point and was wrong here once.
 * A `derived` tile is a COMPONENT of its metric, so that metric still has to be fetched: six ids
 * reach the page only this way (O-COM-025, O-COM-028, O-COM-029, O-FIN-002, O-FIN-004, O-TEL-002).
 * O-COM-032, O-COM-023 and the three O-HYB ids (007, 013, 014) were among them until the
 * distribution parse turned out to be able to read their segment sets and extra columns; those
 * five and O-COM-031 now ALSO reach the page as `distribution` sources, which `identifiersNamedBy`
 * fans out the same way. The three O-HYB ids keep a `derived` tile beside their segment set — the
 * head of a distribution is a name paired with a share, which no `CardMetric` can carry.
 *
 * Product Readiness' 36 product-scoped ids are NOT here — they are chosen at render time and only
 * one is ever read per page view. `productReadinessIdentifiers()` above is their list. PLA-8815.
 */
/**
 * Every catalogue identifier ONE source names — two for the arithmetic kinds, one for the rest.
 *
 * Exported because `data/assembleCard.ts` needs the same fan-out to decide what a card fetches, and
 * a second copy of this switch is exactly how a new kind gets provisioned in one place and not the
 * other: an operand that is never fetched leaves the tile unresolvable rather than approximate.
 * `registry/__tests__/metricIdentifiers.test.ts` keeps its OWN transcription of this on purpose —
 * importing it there would assert only that the file equals itself.
 */
export const identifiersNamedBy = (source: MetricSource): string[] =>
  source.kind === 'metric' || source.kind === 'distribution' || source.kind === 'segmentTotal'
    ? [source.identifier]
    : source.kind === 'derived'
    ? [source.from]
    : source.kind === 'ratio'
    ? [source.numerator.from, source.denominator.from]
    : source.kind === 'difference'
    ? [source.minuend.from, source.subtrahend.from]
    : [];

/**
 * The catalogue id that stands for a whole card: the first one its tiles name, in the registry's own
 * render order. `null` for a card whose every tile is `unmapped`, and for Product Readiness, whose
 * id is chosen from the band dropdown at render time and so is not a property of the card.
 *
 * There is no "primary" flag to read — nothing in the registry declares one, and none of the cards
 * is single-metric — so this is the first tile drawn, not a claim about which metric matters most.
 * Orbit sends it as `context.metric_id` for the card the reader is looking at (PLA-8844); FR §4.3
 * admits only an id that exists in the deployed catalogue, which is why a card that names none sends
 * no field rather than its title.
 */
export const primaryMetricIdFor = (cardKey: string): string | null =>
  metricIdentitiesFor(cardKey).flatMap(entry => identifiersNamedBy(entry.source))[0] ?? null;

/**
 * The reverse of `primaryMetricIdFor`: the catalogue entry ONE identifier stands for, so a reader
 * holding only an id can print the metric's business name and format it the way the cards do.
 * Orbit needs both (PLA-9563 §3).
 *
 * Only the three kinds whose entry IS that identifier are indexed. A `ratio` or a `difference`
 * NAMES two operand ids but is labelled after the quotient, so indexing its operands would answer
 * "Repeat Purchase Rate" for the id of the purchase count underneath it. Product Readiness is left
 * out for the same reason in reverse: its four tiles all read one id and carry four different
 * labels, so no single name belongs to it.
 */
let identityIndex: Map<string, MetricIdentity> | null = null;

const identityByIdentifier = (): Map<string, MetricIdentity> => {
  if (identityIndex === null) {
    const index = new Map<string, MetricIdentity>();

    Object.entries(metricIdentities).forEach(([cardKey, entries]) => {
      if (cardKey === PRODUCT_READINESS_CARD_KEY) {
        return;
      }

      entries.forEach(entry => {
        const { source } = entry;
        const names =
          source.kind === 'metric' ||
          source.kind === 'distribution' ||
          source.kind === 'segmentTotal';

        if (names && !index.has(source.identifier)) {
          index.set(source.identifier, entry);
        }
      });
    });

    identityIndex = index;
  }

  return identityIndex;
};

export const metricIdentityFor = (identifier: string): MetricIdentity | null =>
  identityByIdentifier().get(identifier) ?? null;

export const liveIdentifiers = (): string[] =>
  Array.from(
    new Set(
      Object.values(metricIdentities)
        .flat()
        .flatMap(entry => identifiersNamedBy(entry.source))
    )
  ).sort();

/** The subset whose tiles render straight from `metric_value` — what can ship before Q-B lands. */
export const directlyServableIdentifiers = (): string[] =>
  Array.from(
    new Set(
      Object.values(metricIdentities)
        .flat()
        .filter(entry => entry.source.kind === 'metric')
        .map(entry => (entry.source as { identifier: string }).identifier)
    )
  ).sort();
